import { Button, Icon } from "@blueprintjs/core"
import CustomDialog from "components/Atoms/CustomDialog"
import dayjs from "dayjs"
import { useApproveOrderTransactionMutation } from "generated/graphql"
import React, { useMemo, useState } from "react"
import { ApolloErrorToast, ErrorToast, SuccessToast } from "utils/AppToaster"
import clsx from "utils/clsx"
import { inr } from "utils/formatters.utils"
import styles from "./PaymentTransactionModal.module.scss"

// Values accepted by the `orderTransactionApproval` mutation
export const APPROVAL_STATUS = {
  APPROVED: "APR",
  REJECTED: "REJ",
} as const

type ApprovalStatus = typeof APPROVAL_STATUS[keyof typeof APPROVAL_STATUS]

const getVerificationStatus = (metadata: any): string | undefined => {
  try {
    const parsed =
      typeof metadata === "string" ? JSON.parse(metadata) : metadata
    return parsed?.payment_verification
  } catch {
    return undefined
  }
}

type PaymentTransactionModalProps = {
  /**
   * Order (from paymentVerificationOrders) whose transactions are reviewed
   */
  order?: any
  isOpen: boolean
  onClose: () => void
}

const LabelValue = ({ label, hint, children }) => (
  <div>
    <div className="vt-muted text-xs mb-1">{label}</div>
    <div className="font-600 text-base">{children}</div>
    {hint && <div className="vt-muted text-xs mt-1">{hint}</div>}
  </div>
)

const StatusDot = ({ complete }: { complete: boolean }) => (
  <span className="inline-flex items-center">
    <span
      className={clsx(
        "inline-block rounded-full mr-1",
        complete ? "bg-green-600" : "bg-sky-600"
      )}
      style={{ width: 8, height: 8 }}
    />
    {complete ? "Completed" : "Partial"}
  </span>
)

const fileNameFromUrl = (url?: string) => {
  try {
    return decodeURIComponent(url?.split("?")[0].split("/").pop() ?? "")
  } catch (e) {
    return ""
  }
}

const PaymentTransactionModal = ({
  order,
  isOpen,
  onClose,
}: PaymentTransactionModalProps) => {
  const [selectedId, setSelectedId] = useState<string>(null)
  const [approve, { loading }] = useApproveOrderTransactionMutation({
    refetchQueries: ["PaymentVerificationOrders"],
    awaitRefetchQueries: true,
    onError: err => ApolloErrorToast(err),
  })

  // Installments in the order they were paid, each with the running total
  const installments = useMemo(() => {
    const sorted = [...(order?.transactions ?? [])]
      .filter(Boolean)
      .sort(
        (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf()
      )
    let paid = 0
    return sorted.map(t => {
      paid += t.amount ?? 0
      const outstanding = Math.max((order?.totalAmount ?? 0) - paid, 0)
      return {
        ...t,
        outstanding,
        complete: outstanding === 0,
        verificationStatus: getVerificationStatus(t.metadata),
      }
    })
  }, [order])

  const selected =
    installments.find(t => t.id === selectedId) ??
    installments[installments.length - 1]

  const verificationStatus = selected?.verificationStatus
  const isApproved = verificationStatus === APPROVAL_STATUS.APPROVED
  const isRejected = verificationStatus === APPROVAL_STATUS.REJECTED
  const isReviewed = isApproved || isRejected

  const submit = (approvalStatus: ApprovalStatus) => {
    if (!selected || isReviewed || loading) return
    approve({
      variables: { orderTransactionId: selected.id, approvalStatus },
    }).then(res => {
      // onError already toasted; only continue when the mutation returned data
      if (!res?.data?.orderTransactionApproval) return
      SuccessToast(
        approvalStatus === APPROVAL_STATUS.APPROVED
          ? "Transaction approved"
          : "Transaction rejected"
      )
      setSelectedId(null)
      onClose()
    })
  }

  const handleClose = () => {
    setSelectedId(null)
    onClose()
  }

  const attachmentUrl = selected?.attachment?.url

  const copyReference = async () => {
    if (!selected?.referenceId) return
    try {
      await navigator.clipboard.writeText(selected.referenceId)
      SuccessToast("Transaction reference copied")
    } catch {
      ErrorToast("Unable to copy transaction reference")
    }
  }

  return (
    <CustomDialog
      light
      isOpen={isOpen}
      onClose={handleClose}
      icon="calendar"
      title="Transaction Details"
      width="720px"
      scrollable={false}
      scrollbar={false}
      style={{ height: "90vh" }}
      footerShowCloseButton={false}
      footerRightButton={
        <>
          <Button
            intent="danger"
            icon="cross"
            text="Reject"
            className="mr-3"
            disabled={!selected || isReviewed || loading}
            loading={loading}
            onClick={() => submit(APPROVAL_STATUS.REJECTED)}
          />
          <Button
            intent="success"
            icon="tick-circle"
            text="Approval"
            disabled={!selected || isReviewed || loading}
            loading={loading}
            onClick={() => submit(APPROVAL_STATUS.APPROVED)}
          />
        </>
      }
    >
      {!selected ? (
        <div className="vt-muted p-4 text-center">
          This order does not have any transactions
        </div>
      ) : (
        <>
          {/* Payment history */}
          <div className="border rounded p-3 mb-4 bg-gray-50">
            <div className="flex justify-between text-[10px] tracking-wide mb-2">
              <span className="font-600 uppercase vt-muted">
                Payment History ({installments.length}{" "}
                {installments.length === 1
                  ? "installment RECONCILED"
                  : "installments RECONCILED"}
                )
              </span>
              <span className="vt-muted">
                Click to switch payment entry &amp; receipt attachment
              </span>
            </div>
            <div
              className={clsx(
                "flex gap-2 overflow-x-scroll pb-2",
                styles.paymentHistory
              )}
            >
              {installments.map((t, idx) => {
                const isLast = idx === installments.length - 1
                const active = t.id === selected.id
                const approved =
                  t.verificationStatus === APPROVAL_STATUS.APPROVED
                const rejected =
                  t.verificationStatus === APPROVAL_STATUS.REJECTED
                return (
                  <div
                    key={t.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedId(t.id)}
                    onKeyDown={e => e.key === "Enter" && setSelectedId(t.id)}
                    className={clsx(
                      "rounded-xl p-2 cursor-pointer flex-shrink-0 border-solid",
                      active
                        ? "border-2 border-green bg-green-50"
                        : "border-2 border-gray-300 bg-white"
                    )}
                    style={{ minWidth: 160 }}
                  >
                    <div className="flex justify-between gap-2 text-xs mb-1">
                      <span className="font-500">
                        Part {idx + 1} •{" "}
                        {isLast ? "Final" : dayjs(t.createdAt).format("DD MMM")}
                      </span>
                      <StatusDot complete={t.complete} />
                    </div>
                    <div className="font-600">
                      {inr(t.amount)}{" "}
                      <span
                        className={clsx(
                          "inline-flex items-center text-xs font-600",
                          approved
                            ? " text-green-600"
                            : rejected
                            ? " text-red-600"
                            : " text-yellow-600"
                        )}
                      >
                        <Icon icon={"dot"} size={12} />
                        {approved ? "Paid" : rejected ? "Unpaid" : "Pending"}
                      </span>
                    </div>
                    <div className="vt-muted text-xs truncate">
                      {t.referenceId}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Transaction summary */}
          <div className="flex justify-between mb-2">
            <span className="text-xs font-600 uppercase vt-muted flex items-center">
              <Icon icon="settings" size={12} className="mr-1" />
              Transaction Summary
            </span>
            <span className="text-xs vt-muted mr-2">
              Order Ref:{" "}
              <span className="font-600 text-black">
                #{order?.trackingCode}
              </span>
            </span>
          </div>
          <div
            className="border p-4 mb-4 rounded-xl bg-slate-100"
            style={{ border: "1px solid #ccc" }}
          >
            <LabelValue label="Transaction ID" hint="">
              <div className="flex items-center gap-2">
                <span className="font-mono">
                  {selected.referenceId || "--"}
                </span>
                <Button
                  minimal
                  small
                  icon="duplicate"
                  aria-label="Copy transaction reference"
                  title="Copy transaction reference"
                  disabled={!selected.referenceId}
                  onClick={copyReference}
                />
              </div>
            </LabelValue>
            <hr className="my-3" />
            <div className="grid grid-cols-2 gap-4">
              <LabelValue label="Order Amount" hint="Total invoice balance">
                {inr(order?.totalAmount ?? 0)}
              </LabelValue>
              <LabelValue
                label="Payment Amount"
                hint="Current transaction entry"
              >
                <div className="flex items-center gap-2">
                  <span className={isRejected ? "vt-danger" : "vt-success"}>
                    {inr(selected.amount)}
                  </span>
                  <span
                    className={clsx(
                      "text-xs px-2 py-1 rounded-full",
                      isApproved
                        ? "vt-success bg-green-50"
                        : isRejected
                        ? "vt-danger bg-red-50"
                        : "vt-muted bg-slate-100"
                    )}
                  >
                    {isApproved ? "Paid" : isRejected ? "Unpaid" : ""}
                  </span>
                </div>
              </LabelValue>
              <LabelValue
                label="Outstanding Amount"
                hint="Due after this approval / Reconciled"
              >
                {inr(selected.outstanding)}
              </LabelValue>
              <LabelValue
                label="Payment Status"
                hint={
                  selected.complete ? "Fully paid & verified" : "Partly paid"
                }
              >
                <StatusDot complete={selected.complete} />
              </LabelValue>
            </div>
          </div>

          {/* Attachment */}
          <div className="flex justify-between items-center mb-2">
            <span className="font-600 flex items-center">
              <Icon icon="paperclip" size={12} className="mr-1" />
              Payment Attachment
            </span>
            <span className="flex items-center text-[10px] text-gray-600">
              <Icon
                icon="info-sign"
                size={12}
                className="mr-1 "
                style={{ color: "#2B60AF" }}
              />
              Verify the payment receipt before approving this transaction.
            </span>
          </div>
          <div className="rounded-xl" style={{ border: "1px solid #ccc" }}>
            <div className="text-xs px-3 py-2 border-b bg-slate-50 rounded-t-xl">
              <span className="font-600">Uploaded Payment Receipt</span>
              {attachmentUrl && (
                <span className="vt-muted">
                  {" "}
                  • {fileNameFromUrl(attachmentUrl)}
                </span>
              )}
            </div>
            <div className="p-4 bg-slate-100 rounded-b-xl text-center min-h-[150px]">
              {attachmentUrl ? (
                <a
                  href={attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open payment receipt in a new window"
                >
                  <img
                    alt="Payment receipt"
                    src={attachmentUrl}
                    className="max-w-full h-auto inline-block cursor-pointer"
                    style={{ maxHeight: 420 }}
                  />
                </a>
              ) : (
                <span className="vt-muted">No receipt attached</span>
              )}
            </div>
          </div>
        </>
      )}
    </CustomDialog>
  )
}

export default PaymentTransactionModal
